/**
 * Edit Speurtocht Screen
 * Edit speurtocht settings
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Switch,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  useSpeurttochtAdmin,
  Speurtocht,
  SpeurttochtStatus,
  SpeurttochtNotificationConfig,
  DEFAULT_NOTIFICATION_CONFIG,
} from "@/modules/speurtocht";
import { LottiePicker } from "@/components/LottiePicker";

/** Available icons for speurtocht */
const AVAILABLE_ICONS = [
  "map",
  "compass",
  "navigate",
  "footsteps",
  "trail-sign",
  "flag",
  "trophy",
  "star",
  "diamond",
  "flash",
  "rocket",
  "planet",
];

/** Available colors for speurtocht */
const AVAILABLE_COLORS = [
  "#10B981",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
  "#F59E0B",
  "#EF4444",
  "#14B8A6",
  "#6366F1",
];

/** Status options */
const STATUS_OPTIONS: { value: SpeurttochtStatus; label: string; color: string }[] = [
  { value: "draft", label: "Concept", color: "#F59E0B" },
  { value: "active", label: "Actief", color: "#10B981" },
  { value: "paused", label: "Gepauzeerd", color: "#6B7280" },
  { value: "completed", label: "Afgerond", color: "#8B5CF6" },
  { value: "archived", label: "Gearchiveerd", color: "#6B7280" },
];

export default function EditSpeurttochtScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { fetchSpeurtocht, updateSpeurtocht, isSubmitting, error } =
    useSpeurttochtAdmin();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [speurtocht, setSpeurtocht] = useState<Speurtocht | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("map");
  const [color, setColor] = useState("#10B981");
  const [status, setStatus] = useState<SpeurttochtStatus>("draft");
  const [hasTimeLimit, setHasTimeLimit] = useState(false);
  const [maxDuration, setMaxDuration] = useState("60");
  const [pointsPerStep, setPointsPerStep] = useState("20");
  const [completionBonus, setCompletionBonus] = useState("100");
  const [firstTeamBonus, setFirstTeamBonus] = useState("50");
  const [requirePhotoVerification, setRequirePhotoVerification] = useState(false);
  const [requireLocationVerification, setRequireLocationVerification] = useState(false);
  const [showMapToUsers, setShowMapToUsers] = useState(true);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [completionLottie, setCompletionLottie] = useState<string | null>(null);
  
  // Notification config state
  const [notificationConfig, setNotificationConfig] = useState<SpeurttochtNotificationConfig>(DEFAULT_NOTIFICATION_CONFIG);

  /**
   * Load speurtocht data
   */
  const loadSpeurtocht = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    const data = await fetchSpeurtocht(id);

    if (data) {
      setSpeurtocht(data);
      setName(data.name);
      setDescription(data.description || "");
      setIcon(data.icon);
      setColor(data.color);
      setStatus(data.status);
      setHasTimeLimit(data.has_time_limit);
      setMaxDuration(String(data.max_duration_minutes || 60));
      setPointsPerStep(String(data.points_per_step));
      setCompletionBonus(String(data.completion_bonus));
      setFirstTeamBonus(String(data.first_team_bonus));
      setRequirePhotoVerification(data.require_photo_verification);
      setRequireLocationVerification(data.require_location_verification);
      setShowMapToUsers(data.show_map_to_users);
      setShowAllSteps(data.show_all_steps);
      setCompletionLottie(data.step_completion_lottie || null);
      
      // Load notification config with defaults
      setNotificationConfig({
        ...DEFAULT_NOTIFICATION_CONFIG,
        ...data.notification_config,
      });
    }

    setIsLoading(false);
  }, [id, fetchSpeurtocht]);

  useEffect(() => {
    loadSpeurtocht();
  }, [loadSpeurtocht]);

  /**
   * Handle save
   */
  const handleSave = async () => {
    if (!id || !name.trim()) {
      Alert.alert("Fout", "Voer een naam in");
      return;
    }

    const result = await updateSpeurtocht(id, {
      name: name.trim(),
      description: description.trim() || null,
      icon,
      color,
      status,
      has_time_limit: hasTimeLimit,
      max_duration_minutes: hasTimeLimit ? parseInt(maxDuration) || 60 : null,
      points_per_step: parseInt(pointsPerStep) || 20,
      completion_bonus: parseInt(completionBonus) || 100,
      first_team_bonus: parseInt(firstTeamBonus) || 50,
      require_photo_verification: requirePhotoVerification,
      require_location_verification: requireLocationVerification,
      show_map_to_users: showMapToUsers,
      show_all_steps: showAllSteps,
      step_completion_lottie: completionLottie || undefined,
      notification_config: notificationConfig,
    });

    if (result) {
      router.back();
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color="#10B981" />
      </SafeAreaView>
    );
  }

  if (!speurtocht) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <Text style={{ color: colors.text }}>Speurtocht niet gevonden</Text>
        <TouchableOpacity
          onPress={() => router.back()}
          className="mt-4 px-4 py-2 rounded-lg"
          style={{ backgroundColor: colors.surface }}
        >
          <Text style={{ color: colors.primary }}>Terug</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Ionicons name="arrow-back" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Instellingen
            </Text>
          </View>

          <TouchableOpacity
            onPress={handleSave}
            disabled={isSubmitting || !name.trim()}
            className="px-4 py-2 rounded-xl flex-row items-center"
            style={{
              backgroundColor: name.trim() ? "#10B981" : colors.surfaceSecondary,
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons
                  name="checkmark"
                  size={18}
                  color={name.trim() ? "#FFFFFF" : colors.textTertiary}
                />
                <Text
                  className="ml-1 font-bold"
                  style={{ color: name.trim() ? "#FFFFFF" : colors.textTertiary }}
                >
                  Opslaan
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
          {/* Error Message */}
          {error && (
            <View
              className="p-3 rounded-xl mb-4"
              style={{ backgroundColor: colors.error + "20" }}
            >
              <Text style={{ color: colors.error }}>{error}</Text>
            </View>
          )}

          {/* Preview Card */}
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: `${color}15`, borderWidth: 2, borderColor: color }}
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center"
                style={{ backgroundColor: `${color}25` }}
              >
                <Ionicons name={icon as any} size={24} color={color} />
              </View>
              <View className="ml-3 flex-1">
                <Text className="font-bold text-lg" style={{ color: colors.text }}>
                  {name || "Speurtocht naam"}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  {description || "Beschrijving"}
                </Text>
              </View>
            </View>
          </View>

          {/* Status Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Status
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row flex-wrap">
              {STATUS_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  onPress={() => setStatus(option.value)}
                  className="px-3 py-2 rounded-lg mr-2 mb-2 flex-row items-center"
                  style={{
                    backgroundColor:
                      status === option.value ? `${option.color}25` : colors.surfaceSecondary,
                    borderWidth: status === option.value ? 2 : 0,
                    borderColor: option.color,
                  }}
                >
                  <Ionicons
                    name={status === option.value ? "radio-button-on" : "radio-button-off"}
                    size={16}
                    color={status === option.value ? option.color : colors.textSecondary}
                  />
                  <Text
                    className="ml-2 font-medium"
                    style={{
                      color: status === option.value ? option.color : colors.textSecondary,
                    }}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Basic Info Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Basis Informatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            {/* Name */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Naam *
              </Text>
              <TextInput
                value={name}
                onChangeText={setName}
                placeholder="Bijv. De Grote Speurtocht"
                placeholderTextColor={colors.textTertiary}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
              />
            </View>

            {/* Description */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Beschrijving
              </Text>
              <TextInput
                value={description}
                onChangeText={setDescription}
                placeholder="Korte beschrijving"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={3}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                  minHeight: 80,
                  textAlignVertical: "top",
                }}
              />
            </View>

            {/* Icon Selection */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Icoon
              </Text>
              <View className="flex-row flex-wrap">
                {AVAILABLE_ICONS.map((iconName) => (
                  <TouchableOpacity
                    key={iconName}
                    onPress={() => setIcon(iconName)}
                    className="w-10 h-10 rounded-lg items-center justify-center mr-2 mb-2"
                    style={{
                      backgroundColor:
                        icon === iconName ? `${color}25` : colors.surfaceSecondary,
                      borderWidth: icon === iconName ? 2 : 0,
                      borderColor: color,
                    }}
                  >
                    <Ionicons
                      name={iconName as any}
                      size={20}
                      color={icon === iconName ? color : colors.textSecondary}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Color Selection */}
            <View>
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Kleur
              </Text>
              <View className="flex-row flex-wrap">
                {AVAILABLE_COLORS.map((colorOption) => (
                  <TouchableOpacity
                    key={colorOption}
                    onPress={() => setColor(colorOption)}
                    className="w-10 h-10 rounded-lg items-center justify-center mr-2 mb-2"
                    style={{
                      backgroundColor: colorOption,
                      borderWidth: color === colorOption ? 3 : 0,
                      borderColor: colors.text,
                    }}
                  >
                    {color === colorOption && (
                      <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Time Limit Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Tijdslimiet
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Tijdslimiet inschakelen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Teams hebben beperkte tijd
                </Text>
              </View>
              <Switch
                value={hasTimeLimit}
                onValueChange={setHasTimeLimit}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={hasTimeLimit ? "#10B981" : colors.surfaceSecondary}
              />
            </View>

            {hasTimeLimit && (
              <View>
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Maximale duur (minuten)
                </Text>
                <TextInput
                  value={maxDuration}
                  onChangeText={setMaxDuration}
                  keyboardType="number-pad"
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
              </View>
            )}
          </View>

          {/* Points Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Punten
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row mb-4">
              <View className="flex-1 mr-2">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Per stap
                </Text>
                <TextInput
                  value={pointsPerStep}
                  onChangeText={setPointsPerStep}
                  keyboardType="number-pad"
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
              </View>
              <View className="flex-1 ml-2">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Voltooiings bonus
                </Text>
                <TextInput
                  value={completionBonus}
                  onChangeText={setCompletionBonus}
                  keyboardType="number-pad"
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
              </View>
            </View>

            <View>
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Eerste team bonus
              </Text>
              <TextInput
                value={firstTeamBonus}
                onChangeText={setFirstTeamBonus}
                keyboardType="number-pad"
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
              />
            </View>
          </View>

          {/* Verification Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Verificatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Foto verificatie
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Admin keurt foto's goed
                </Text>
              </View>
              <Switch
                value={requirePhotoVerification}
                onValueChange={setRequirePhotoVerification}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={requirePhotoVerification ? "#10B981" : colors.surfaceSecondary}
              />
            </View>

            <View className="flex-row items-center justify-between">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Locatie verificatie
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Admin keurt locaties goed
                </Text>
              </View>
              <Switch
                value={requireLocationVerification}
                onValueChange={setRequireLocationVerification}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={requireLocationVerification ? "#10B981" : colors.surfaceSecondary}
              />
            </View>
          </View>

          {/* Display Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Weergave
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Kaart tonen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Interactieve kaart bij locaties
                </Text>
              </View>
              <Switch
                value={showMapToUsers}
                onValueChange={setShowMapToUsers}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={showMapToUsers ? "#10B981" : colors.surfaceSecondary}
              />
            </View>

            <View className="flex-row items-center justify-between">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Alle stappen tonen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Of alleen huidige stap
                </Text>
              </View>
              <Switch
                value={showAllSteps}
                onValueChange={setShowAllSteps}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={showAllSteps ? "#10B981" : colors.surfaceSecondary}
              />
            </View>
          </View>

          {/* Lottie Animation */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Animatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <LottiePicker
              value={completionLottie}
              onChange={setCompletionLottie}
              label="Standaard voltooiings animatie"
              placeholder="Kies een animatie"
            />
            <Text className="text-xs mt-2" style={{ color: colors.textTertiary }}>
              Wordt gebruikt als stappen geen eigen animatie hebben
            </Text>
          </View>

          {/* Activation Notification */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Activatie Notificatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Notificatie versturen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Bij activeren speurtocht
                </Text>
              </View>
              <Switch
                value={notificationConfig.on_activated?.enabled ?? true}
                onValueChange={(enabled) =>
                  setNotificationConfig((prev) => ({
                    ...prev,
                    on_activated: { ...prev.on_activated, enabled },
                  }))
                }
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={
                  notificationConfig.on_activated?.enabled ? "#10B981" : colors.surfaceSecondary
                }
              />
            </View>

            {notificationConfig.on_activated?.enabled && (
              <>
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Titel
                  </Text>
                  <TextInput
                    value={notificationConfig.on_activated?.title || ""}
                    onChangeText={(title) =>
                      setNotificationConfig((prev) => ({
                        ...prev,
                        on_activated: { ...prev.on_activated, title },
                      }))
                    }
                    placeholder="🗺️ Nieuwe Speurtocht!"
                    placeholderTextColor={colors.textTertiary}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                  />
                </View>

                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Bericht
                  </Text>
                  <TextInput
                    value={notificationConfig.on_activated?.body || ""}
                    onChangeText={(body) =>
                      setNotificationConfig((prev) => ({
                        ...prev,
                        on_activated: { ...prev.on_activated, body },
                      }))
                    }
                    placeholder='"{speurtocht_name}" is nu beschikbaar!'
                    placeholderTextColor={colors.textTertiary}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                    multiline
                    numberOfLines={2}
                  />
                  <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                    Gebruik {"{speurtocht_name}"} voor de naam
                  </Text>
                </View>

                <LottiePicker
                  value={notificationConfig.on_activated?.lottie_animation || null}
                  onChange={(lottie_animation) =>
                    setNotificationConfig((prev) => ({
                      ...prev,
                      on_activated: { ...prev.on_activated, lottie_animation: lottie_animation || "party" },
                    }))
                  }
                  label="Notificatie animatie"
                  placeholder="party"
                />
              </>
            )}
          </View>

          <View className="h-32" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

